import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle2, FlaskConical, XCircle } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import {
  buildDefaultArgs,
  coerceArgs,
  describeError,
  getSecretLabel,
  isOauthServer,
  isPerUser,
  prettyJson,
  schemaToFields,
} from '../mcpUtils';

/**
 * Tool test panel (§5).
 *
 * The test runs the real upstream call through the exact path a live MCP call
 * takes, so a green result means the credential, URL and schema all actually
 * work — it is the cheapest way to find a broken connector before publishing.
 *
 * Errors are shown verbatim here, unlike the public endpoint: this is the
 * owner's own API, so there is nothing to leak to them.
 */
const ToolTestPanel = ({ server, tools, initialToolId, notify }) => {
  const [toolId, setToolId] = useState(initialToolId || tools[0]?.id || '');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [credential, setCredential] = useState('');
  const [credentialError, setCredentialError] = useState(null);

  // A per-user server stores no key, so a test has to be told which user's key
  // to act as. It is sent for this one call and never saved. A sign-in server
  // is the same shape, except what's supplied is a token from the provider.
  const perUser = isPerUser(server);
  const oauth = isOauthServer(server);

  const tool = useMemo(() => tools.find((t) => String(t.id) === String(toolId)), [tools, toolId]);
  const fields = useMemo(() => schemaToFields(tool?.input_schema), [tool]);

  useEffect(() => {
    if (initialToolId) setToolId(initialToolId);
  }, [initialToolId]);

  useEffect(() => {
    setValues(buildDefaultArgs(fields));
    setErrors({});
    setResult(null);
  }, [fields, toolId]);

  const setValue = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const handleRun = async () => {
    const { args, errors: fieldErrors } = coerceArgs(fields, values);
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
      return;
    }
    setRunning(true);
    setResult(null);
    setCredentialError(null);
    try {
      const data = await mcpServerService.testTool(
        server.id,
        tool.id,
        args,
        perUser && credential.trim() ? credential.trim() : undefined
      );
      setResult(data);
    } catch (e) {
      // "no stored key to test with" is a problem with the credential field,
      // not with the page — surface it there rather than as a failed run.
      const message = describeError(e, 'The test call failed.');
      if (e?.status === 400 && /credential|stored key/i.test(message)) {
        setCredentialError(message);
      } else {
        setResult({ success: false, error: message, reference: e?.data?.reference });
      }
    } finally {
      setRunning(false);
    }
  };

  if (tools.length === 0) {
    return (
      <div className={sharedStyles.emptyState}>
        <FlaskConical size={44} className={sharedStyles.emptyIcon} />
        <Typography variant="h6">Nothing to test yet</Typography>
        <Typography variant="body2" className={sharedStyles.muted}>
          Import or create tools first.
        </Typography>
      </div>
    );
  }

  const renderField = (field) => {
    const error = errors[field.name];
    const helper = error || field.description || (field.required ? 'Required' : '');

    if (field.kind === 'boolean') {
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Switch
              checked={!!values[field.name]}
              onChange={(e) => setValue(field.name, e.target.checked)}
            />
          }
          label={`${field.label}${field.required ? ' *' : ''}`}
        />
      );
    }

    if (field.kind === 'enum') {
      return (
        <FormControl key={field.name} size="small" fullWidth error={!!error}>
          <InputLabel>{`${field.label}${field.required ? ' *' : ''}`}</InputLabel>
          <Select
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={values[field.name] ?? ''}
            onChange={(e) => setValue(field.name, e.target.value)}
          >
            <MenuItem value="">
              <em>Not set</em>
            </MenuItem>
            {field.options.map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {String(opt)}
              </MenuItem>
            ))}
          </Select>
          {helper && (
            <Typography variant="caption" sx={{ mt: 0.5, color: error ? 'error.main' : 'text.secondary' }}>
              {helper}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.kind === 'json') {
      return (
        <Box key={field.name}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {field.label}
            {field.required ? ' *' : ''}
          </Typography>
          <textarea
            className={styles.codeArea}
            style={{ minHeight: 110 }}
            value={values[field.name] ?? ''}
            onChange={(e) => setValue(field.name, e.target.value)}
            placeholder={field.type === 'array' ? '[]' : '{}'}
            spellCheck={false}
          />
          {helper && (
            <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary' }}>
              {helper}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <TextField
        key={field.name}
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={values[field.name] ?? ''}
        onChange={(e) => setValue(field.name, e.target.value)}
        type={field.kind === 'number' ? 'number' : 'text'}
        size="small"
        fullWidth
        error={!!error}
        helperText={helper}
      />
    );
  };

  return (
    <>
      <div className={styles.section}>
        <Typography className={styles.sectionTitle}>Test a tool</Typography>
        <span className={styles.sectionHint}>
          This makes the real call to your upstream API through the same path a live MCP request
          takes. A green result means the credential, URL and schema all work.
        </span>

        <FormControl size="small" fullWidth>
          <InputLabel>Tool</InputLabel>
          <Select label="Tool" value={toolId} onChange={(e) => setToolId(e.target.value)}>
            {tools.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.tool_name}
                {!t.enabled ? ' — not exposed' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {tool?.description && <span className={styles.sectionHint}>{tool.description}</span>}

        {perUser && (
          <TextField
            label={oauth ? 'Test with an access token' : 'Test with this key'}
            value={credential}
            onChange={(e) => {
              setCredential(e.target.value);
              setCredentialError(null);
            }}
            type="password"
            size="small"
            fullWidth
            error={!!credentialError}
            helperText={
              credentialError ||
              (oauth
                ? "There's no stored key on this server. Paste an access token from your own login system to test with — it isn't saved."
                : `There's no stored key on this server. Paste any valid user key to test with — it isn't saved.${
                    server.upstream_auth_type === 'BASIC_AUTH'
                      ? ' For Basic auth use username:password.'
                      : ''
                  }`)
            }
            placeholder={oauth ? 'eyJhbGciOi…' : getSecretLabel(server.upstream_auth_type)}
          />
        )}

        {fields.length === 0 ? (
          <Alert severity="info">This tool takes no arguments.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{fields.map(renderField)}</Box>
        )}

        <Box>
          <Button
            className={sharedStyles.primaryBtn}
            variant="contained"
            onClick={handleRun}
            disabled={running || !tool}
            startIcon={running ? <CircularProgress size={15} color="inherit" /> : <FlaskConical size={16} />}
          >
            {running ? 'Running…' : 'Run test'}
          </Button>
        </Box>
      </div>

      {result && (
        <div
          className={`${styles.testResult} ${
            result.success ? styles.testResultOk : styles.testResultError
          }`}
        >
          <div className={styles.testResultHead}>
            {result.success ? (
              <>
                <CheckCircle2 size={18} color="#10b981" />
                <span style={{ color: '#10b981' }}>Success</span>
              </>
            ) : (
              <>
                <XCircle size={18} color="#ef4444" />
                <span style={{ color: '#ef4444' }}>Failed</span>
              </>
            )}
          </div>

          <div className={styles.testMeta}>
            {result.execution_time_ms !== undefined && <span>{result.execution_time_ms} ms</span>}
            {result.reference && (
              <span>
                reference <code className={styles.reference}>{result.reference}</code>
              </span>
            )}
          </div>

          {result.success ? (
            <pre className={styles.codeBlock}>{prettyJson(result.result)}</pre>
          ) : (
            <pre className={`${styles.codeBlock} ${styles.codeBlockWrap}`}>
              {typeof result.error === 'string' ? result.error : prettyJson(result.error)}
            </pre>
          )}
        </div>
      )}
    </>
  );
};

export default ToolTestPanel;
